import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDeliveryStatusByMark } from '../../api/hooks/delivery/useDeliveryStatusByMark';
import { Loading } from '../../components/atoms/Loading';
import { STANDALONE_FEATURE } from '../../interfaces/enumerables';
import { canConfirmDeliveryReturn } from '../../services/utils/deliveryStatusUtils';
import { useHasActiveFeature } from '../../services/utils/useHasActiveFeature';
import { DeliveryConfirmReturnForm } from './component/DeliveryConfirmReturnForm';
import { DeliveryInstructionsLink } from './component/DeliveryInstructionsLink';
import { DeliveryStatusGsisDoc } from './component/DeliveryStatusGsisDoc';
import { DeliveryStatusSteps } from './component/DeliveryStatusSteps';
import { ProviderInvoiceDoc } from './component/ProviderInvoiceDoc';

export const NewDeliveryReturnScreen = () => {
  const { isChecking: isFeatureChecking } = useHasActiveFeature(STANDALONE_FEATURE.TRANSIT, true);
  const { t } = useTranslation();
  const { mark } = useParams<{ mark: string }>();
  const { data, isLoading, isRefetching } = useDeliveryStatusByMark(mark);

  const getLoading = useCallback(() => {
    if (isLoading || isRefetching || isFeatureChecking) {
      return <Loading backgroundFull />;
    }

    return null;
  }, [isLoading, isRefetching, isFeatureChecking]);

  const getErrorCode = useCallback(() => {
    if (data?.errorCode && !data?.extraData?.doc) {
      return (
        <div className="m-t-16 m-b-16 flex-center-center-column-nowrap width-100">
          <div className="boldText4 text-center text-danger text-with-new-line max-width-900">
            {t(`API_ERRORS.${data?.errorCode}`)}
          </div>
        </div>
      );
    }

    return null;
  }, [data?.errorCode, data?.extraData?.doc, t]);

  const getCurrentStatus = useCallback(() => {
    if (data?.data) {
      return (
        <div className="margin-b-16 flex-center-center-column-nowrap width-100">
          <div className="boldText6">{t('DELIVERY.DELIVERY_STATUS')}</div>
          <div className="margin-t-16 simpleBorder p-12">
            <div className="normalText5 m-b-6">
              <b>{t('DELIVERY.NEW_DELIVERY_MARK_PLACEHOLDER')}</b>: {data?.data?.invoiceMark}
            </div>
            <div className="normalText5">
              <b>{t('DELIVERY.DELIVERY_STATUS_CURRENT')}</b>: {t(`DELIVERY.STATUS.${data?.data?.status}`)}
            </div>
          </div>
        </div>
      );
    }
  }, [data?.data, t]);

  const getGsisDocData = useCallback(() => {
    if (data?.extraData?.doc) {
      return (
        <div className="m-b-32 flex-center-center-column-nowrap width-100">
          <DeliveryStatusGsisDoc doc={data?.extraData?.doc} />
        </div>
      );
    }
    return null;
  }, [data?.extraData?.doc]);

  const getProviderInvoiceData = useCallback(() => {
    const qrUrl = data?.extraData?.doc?.qrCodeUrl;
    if (!qrUrl) return null;
    return (
      <div className="m-b-32 flex-center-center-column-nowrap width-100">
        <ProviderInvoiceDoc qrUrl={qrUrl} />
      </div>
    );
  }, [data?.extraData?.doc?.qrCodeUrl]);

  const getSteps = useCallback(() => {
    if (data?.data?.lifecycleHistory) {
      return (
        <div className="m-b-32 flex-center-center-column-nowrap width-100">
          <DeliveryStatusSteps lifecycleHistory={data?.data?.lifecycleHistory} />
        </div>
      );
    }
  }, [data?.data?.lifecycleHistory]);

  const getActionForm = useCallback(() => {
    if (!isLoading && canConfirmDeliveryReturn(data?.extraData?.isIssuer, data?.data, data?.extraData?.doc)) {
      return (
        <div className="m-b-32 flex-center-center-column-nowrap width-100">
          <div className="flex-center-center-column-nowrap">
            <DeliveryConfirmReturnForm
              // Resolved from the fetched doc, not the raw route param -- `mark` is the literal
              // string "create" while on that route, which would otherwise get passed straight
              // through as a (truthy) mark and break the form's qrUrl-only status panel
              mark={data?.extraData?.doc?.mark ? String(data.extraData.doc.mark) : undefined}
              qrUrl={data?.extraData?.doc?.qrCodeUrl}
            />
          </div>
        </div>
      );
    }
  }, [data?.data, data?.extraData?.doc, data?.extraData?.isIssuer, isLoading]);

  return (
    <div className="scrollable">
      {getLoading()}
      <div className="width-100 p-t-16 p-b-16 flex-center-start-column-nowrap p-r-24 p-l-24">
        <DeliveryInstructionsLink />
        {getErrorCode()}
        {getCurrentStatus()}
        {getActionForm()}
        {getSteps()}
        {getGsisDocData()}
        {getProviderInvoiceData()}
      </div>
    </div>
  );
};
